import { Urls } from 'src/appConstants';
import {
	getSentryEventCreationState,
	type SentryEventCreationFlow,
	type SentryEventCreationStep,
} from 'src/modules/sentry-event-creation-state';

export type SentryArea =
	| 'auth'
	| 'event_page'
	| 'event_creation'
	| 'event_management'
	| 'payout_account'
	| 'checkout'
	| 'home'
	| 'panel'
	| 'other';

export type SentryErrorSource =
	| 'render'
	| 'api'
	| 'form'
	| 'state'
	| 'storage'
	| 'integration'
	| 'navigation'
	| 'unknown';

export type SentryRouteContext = {
	area: SentryArea;
	flow: string;
	step: string;
	routeTemplate: string;
	ids: {
		eventId?: string;
		sellId?: string;
		profileSlug?: string;
	};
};

export type SentryAuthApiContext = {
	area: 'auth';
	flow: 'login' | 'signup' | 'recovery' | 'otp' | 'oauth' | 'logout' | 'auth';
	step: string;
};

export type SentryCheckoutErrorDomain =
	| 'auth'
	| 'inventory'
	| 'coupon'
	| 'payment'
	| 'state';

export type SentryCheckoutApiContext = {
	area: 'checkout';
	flow: 'ticket_purchase';
	step: 'tickets' | 'login' | 'otp' | 'payment' | 'pix';
	action: string;
	errorDomain: SentryCheckoutErrorDomain;
	authAction?: 'login' | 'signup' | 'otp' | 'oauth';
};

export type SentryPayoutAccountStep =
	| 'recipient_details'
	| 'bank_account'
	| 'review_submit';

export type SentryPayoutAccountErrorDomain =
	| 'identity'
	| 'address'
	| 'contact'
	| 'bank_account'
	| 'status'
	| 'integration';

export type SentryPayoutAccountApiContext = {
	area: 'payout_account';
	flow: 'recipient_registration';
	step: SentryPayoutAccountStep;
	action: string;
	errorDomain: SentryPayoutAccountErrorDomain;
};

export type SentryEventCreationErrorDomain =
	| 'auth'
	| 'event'
	| 'draft'
	| 'place'
	| 'schedule'
	| 'tickets'
	| 'media'
	| 'publication'
	| 'invariant';

export type SentryEventCreationApiContext = {
	area: 'event_creation';
	flow: SentryEventCreationFlow;
	step: SentryEventCreationStep;
	action: string;
	errorDomain: SentryEventCreationErrorDomain;
	authAction?: 'login' | 'signup' | 'otp' | 'oauth';
};

export type SentryPublicEventErrorDomain =
	| 'event'
	| 'tickets'
	| 'inventory'
	| 'producer'
	| 'coupon'
	| 'contact'
	| 'complaint';

export type SentryPublicEventApiContext = {
	area: 'event_page';
	flow: 'public_event';
	step:
		| 'initial_load'
		| 'tickets'
		| 'cart'
		| 'producer'
		| 'contact'
		| 'complaint';
	action: string;
	errorDomain: SentryPublicEventErrorDomain;
};

export type SentryEventManagementStep =
	| 'overview'
	| 'dashboard'
	| 'sales'
	| 'orders'
	| 'checkin'
	| 'guests'
	| 'guest_lists'
	| 'coupons'
	| 'private_tickets'
	| 'promoters'
	| 'collaborators'
	| 'integrations'
	| 'cart_recovery'
	| 'reports'
	| 'settings'
	| 'unknown';

export type SentryEventManagementErrorDomain =
	| 'dashboard'
	| 'sales'
	| 'orders'
	| 'checkin'
	| 'guests'
	| 'coupons'
	| 'private_tickets'
	| 'promoters'
	| 'collaborators'
	| 'integrations'
	| 'cart_recovery'
	| 'reports'
	| 'settings'
	| 'event'
	| 'unknown';

export type SentryEventManagementApiContext = {
	area: 'event_management';
	flow: 'manage_event';
	step: SentryEventManagementStep;
	action: string;
	errorDomain: SentryEventManagementErrorDomain;
};

type AuthApiContextInput = {
	routeTemplate: string;
	sanitizedUrl: string;
	method: string;
	isAuthApiTarget: boolean;
	isAuthSurface: boolean;
};

type CheckoutApiContextInput = {
	routeTemplate: string;
	method: string;
	isCheckoutSurface: boolean;
};

type PayoutAccountApiContextInput = {
	routeTemplate: string;
	method: string;
	isPayoutAccountSurface: boolean;
	currentStep: SentryPayoutAccountStep;
};

type EventCreationApiContextInput = {
	routeTemplate: string;
	method: string;
	routeContext: SentryRouteContext | null;
};

type PublicEventApiContextInput = {
	routeTemplate: string;
	method: string;
	routeContext: SentryRouteContext | null;
};

type EventManagementApiContextInput = {
	routeTemplate: string;
	method: string;
	routeContext: SentryRouteContext | null;
};

const trimPathSlashes = (pathname: string) =>
	pathname.replace(/^\/+|\/+$/g, '');

const getPathSegments = (pathname: string) =>
	trimPathSlashes(pathname).split('/').filter(Boolean);

const getCheckoutStep = (segments: string[]) => {
	const checkoutIndex = segments.indexOf('checkout');
	const step = checkoutIndex >= 0 ? segments[checkoutIndex + 1] : undefined;

	if (!step) return 'tickets';

	if (step === 'payment-methods' || step === 'add-card' || step === 'confirm-card') {
		return 'payment';
	}

	if (step === 'pix' || step === 'pix-success' || step === 'pix-rejected') {
		return 'pix';
	}

	if (step === 'success') return 'success';
	if (step === 'rejected') return 'rejected';
	if (step === 'otp') return 'otp';
	if (step === 'login') return 'login';
	if (step === 'payment') return 'payment';
	if (step === 'tickets') return 'tickets';

	return 'unknown';
};

const getEventCreationStep = (segments: string[]) => {
	const step = segments[3];

	if (step === 'what') return 'event_what';
	if (step === 'when') return 'event_when';
	if (step === 'where') return 'event_where';
	if (step === 'how-much') return 'event_how_much';

	return 'unknown';
};

const getEventManagementStep = (
	segments: string[]
): SentryEventManagementStep => {
	const section = segments[3];

	if (!section || section === 'overview') return 'overview';
	if (section === 'dashboard') return 'dashboard';
	if (section === 'reports') return 'reports';
	if (section === 'sales-origins') return 'sales';
	if (section === 'sold-tickets') return 'orders';
	if (section === 'checkin') return 'checkin';
	if (section === 'cart-recovery') return 'cart_recovery';
	if (section === 'private-ticket') return 'private_tickets';
	if (section === 'coupons') return 'coupons';
	if (section === 'promoters') return 'promoters';
	if (section === 'listas') return segments[4] ? 'guests' : 'guest_lists';
	if (section === 'collaborators') return 'collaborators';
	if (section === 'integrations') return 'integrations';
	if (section === 'settings') return 'settings';

	return 'unknown';
};

export const getSentryRouteContext = (
	pathname: string
): SentryRouteContext => {
	const normalizedPathname = pathname || '/';
	const segments = getPathSegments(normalizedPathname);

	if (normalizedPathname === '/' || segments.length === 0) {
		return {
			area: 'home',
			flow: 'unknown',
			step: 'unknown',
			routeTemplate: '/',
			ids: {},
		};
	}

	if (segments[0] === 'login') {
		return {
			area: 'auth',
			flow: 'login',
			step: 'login',
			routeTemplate: '/login',
			ids: {},
		};
	}

	if (segments[0] === 'cadastro') {
		return {
			area: 'auth',
			flow: 'signup',
			step: 'signup',
			routeTemplate: '/cadastro',
			ids: {},
		};
	}

	if (segments[0] === 'password-update') {
		return {
			area: 'auth',
			flow: 'recovery',
			step: 'recover_password',
			routeTemplate: '/password-update/:email/:code',
			ids: {},
		};
	}

	if (
		(segments[0] === 'admin' || segments[0] === 'staff') &&
		segments[1] === 'event' &&
		segments[2]
	) {
		return {
			area: 'event_management',
			flow: 'manage_event',
			step: getEventManagementStep(segments),
			routeTemplate: '/:userLevel/event/:eventID/:section',
			ids: { eventId: segments[2] },
		};
	}

	if (segments[0] === 'evento' && segments[1] && segments[2]) {
		const eventId = segments[2];

		if (segments[3] === 'checkout') {
			return {
				area: 'checkout',
				flow: 'ticket_purchase',
				step: getCheckoutStep(segments),
				routeTemplate: '/evento/:eventSlug/:eventID/checkout/:step',
				ids: { eventId },
			};
		}

		return {
			area: 'event_page',
			flow: 'public_event',
			step: 'event_root',
			routeTemplate: '/evento/:eventSlug/:eventID/*',
			ids: { eventId },
		};
	}

	if (segments[0] === 'event' && segments[1]) {
		if (segments[2] === 'checkout') {
			return {
				area: 'checkout',
				flow: 'ticket_purchase',
				step: getCheckoutStep(segments),
				routeTemplate: '/event/:eventID/checkout/:step',
				ids: { eventId: segments[1] },
			};
		}

		return {
			area: 'event_page',
			flow: 'public_event',
			step: 'event_root',
			routeTemplate: '/event/:eventID/*',
			ids: { eventId: segments[1] },
		};
	}

	if (segments[0] === 'me' && segments[1] === 'events' && segments[2]) {
		const activeState = getSentryEventCreationState();
		const isStart = segments[2] === 'start';
		const isBootstrap = segments[2] === 'new';
		const flow =
			activeState?.flow ??
			(isStart || isBootstrap ? 'create_event' : 'edit_event');
		const step = isStart
			? 'event_start'
			: isBootstrap
				? 'event_bootstrap'
				: activeState?.step ?? getEventCreationStep(segments);

		return {
			area: 'event_creation',
			flow,
			step,
			routeTemplate: '/me/events/:id/:step',
			ids: {
				eventId: isStart || isBootstrap ? undefined : segments[2],
			},
		};
	}

	if (segments[0] === 'me' || segments[0] === 'painel') {
		return {
			area: 'panel',
			flow: 'unknown',
			step: 'unknown',
			routeTemplate: `/${segments[0]}/*`,
			ids: {},
		};
	}

	if (segments.length === 1) {
		return {
			area: 'other',
			flow: 'unknown',
			step: 'unknown',
			routeTemplate: '/:profileSlug',
			ids: { profileSlug: segments[0] },
		};
	}

	return {
		area: 'other',
		flow: 'unknown',
		step: 'unknown',
		routeTemplate: '/other',
		ids: {},
	};
};

const getApiTarget = (url?: string) => {
	if (!url) return 'unknown';
	try {
		return new URL(url).host;
	} catch {
		return url;
	}
};

const getEventApiTarget = () => {
	return getApiTarget(Urls?.EVENT_API);
};

const normalizeEventDraftApiRoute = (pathname: string) =>
	pathname
		.replace(/^\/defaults\/ticket$/, '/defaults/ticket')
		.replace(/^\/[^/]+\/tickets\/sort$/, '/:draftId/tickets/sort')
		.replace(/^\/[^/]+\/tickets$/, '/:draftId/tickets')
		.replace(/^\/[^/]+\/ticket\/[^/]+\/(close|resume)$/, '/:draftId/ticket/:ticketId/$1')
		.replace(/^\/[^/]+\/ticket\/[^/]+$/, '/:draftId/ticket/:ticketId')
		.replace(/^\/[^/]+\/ticket$/, '/:draftId/ticket')
		.replace(/^\/[^/]+\/sessions$/, '/:draftId/sessions')
		.replace(/^\/[^/]+\/session\/[^/]+$/, '/:draftId/session/:sessionId')
		.replace(/^\/[^/]+\/session$/, '/:draftId/session')
		.replace(/^\/[^/]+\/(tax|banner)$/, '/:draftId/$1')
		.replace(/^\/[^/]+\/draft$/, '/:eventId/draft')
		.replace(/^\/[^/]+$/, '/:draftId');

/** Paths `/event/...` da user API — nunca passar pelo normalizador de draft. */
const normalizeUserEventApiRoute = (pathname: string) => {
	if (pathname === '/event/drafts' || pathname === '/event/publish') {
		return pathname;
	}

	return pathname
		.replace(
			/\/event\/[^/]+\/coupons\/[^/]+$/,
			'/event/:eventId/coupons/:coupon'
		)
		.replace(/\/event\/[^/]+\/coupons$/, '/event/:eventId/coupons')
		.replace(
			/\/event\/[^/]+\/restricted-link\/[^/]+$/,
			'/event/:eventId/restricted-link/:linkName'
		)
		.replace(
			/\/event\/[^/]+\/restricted-link$/,
			'/event/:eventId/restricted-link'
		)
		.replace(/\/event\/[^/]+\/promoters$/, '/event/:eventId/promoters')
		.replace(/\/event\/[^/]+\/collaborators$/, '/event/:eventId/collaborators')
		.replace(/\/event\/[^/]+\/guests$/, '/event/:eventId/guests')
		.replace(/\/event\/[^/]+\/reports$/, '/event/:eventId/reports')
		.replace(
			/\/event\/[^/]+\/sales-origins$/,
			'/event/:eventId/sales-origins'
		)
		.replace(/\/event\/[^/]+\/ticket-types$/, '/event/:eventId/ticket-types')
		.replace(/\/event\/[^/]+\/banner$/, '/event/:eventId/banner')
		.replace(/\/event\/[^/]+$/, '/event/:eventId');
};

export const normalizeApiRoute = (url?: string, apiTarget?: string) => {
	if (!url) return 'unknown';

	let pathname = url;

	try {
		const fallbackBase =
			typeof window !== 'undefined'
				? window.location.origin
				: 'https://uticket.local';
		pathname = new URL(url, fallbackBase).pathname;
	} catch {
		pathname = url.split('?')[0];
	}

	const normalizedPathname = pathname.startsWith('/')
		? pathname
		: `/${pathname}`;

	// `/event/...` (user API) mesmo se o host coincidir com EVENT_API.
	if (
		normalizedPathname === '/event/drafts' ||
		normalizedPathname === '/event/publish' ||
		normalizedPathname.startsWith('/event/')
	) {
		return normalizeUserEventApiRoute(normalizedPathname);
	}

	const currentEventApiTarget = getEventApiTarget();
	if (
		currentEventApiTarget !== 'unknown' &&
		apiTarget === currentEventApiTarget
	) {
		return normalizeEventDraftApiRoute(normalizedPathname);
	}

	return normalizedPathname
		.replace(
			/\/cart-recovery\/metrics\/[^/]+$/,
			'/cart-recovery/metrics/:eventId'
		)
		.replace(/\/cart-recovery\/[^/]+$/, '/cart-recovery/:cartRecoveryId')
		.replace(/\/cart-recovery\/?$/, '/cart-recovery')
		.replace(/\/checkin\/[^/]+$/, '/checkin/:eventId')
		.replace(/\/eventinfo\/[^/]+$/, '/eventinfo/:eventId')
		.replace(/\/sell\/open$/, '/sell/open')
		.replace(/\/sell\/[^/]+\/status$/, '/sell/:sellId/status')
		.replace(
			/\/sell\/[^/]+\/discount\/[^/]+$/,
			'/sell/:sellId/discount/:coupon'
		)
		.replace(/\/sell\/[^/]+\/(buy|pay|edit|close)$/, '/sell/:sellId/$1')
		.replace(
			/\/event\/[^/]+\/coupons\/[^/]+$/,
			'/event/:eventId/coupons/:coupon'
		)
		.replace(/\/event\/[^/]+\/coupons$/, '/event/:eventId/coupons')
		.replace(
			/\/event\/[^/]+\/restricted-link\/[^/]+$/,
			'/event/:eventId/restricted-link/:linkName'
		)
		.replace(
			/\/event\/[^/]+\/restricted-link$/,
			'/event/:eventId/restricted-link'
		)
		.replace(/\/event\/[^/]+\/promoters$/, '/event/:eventId/promoters')
		.replace(/\/event\/[^/]+\/collaborators$/, '/event/:eventId/collaborators')
		.replace(/\/event\/[^/]+\/guests$/, '/event/:eventId/guests')
		.replace(/\/event\/[^/]+\/reports$/, '/event/:eventId/reports')
		.replace(
			/\/event\/[^/]+\/sales-origins$/,
			'/event/:eventId/sales-origins'
		)
		.replace(/\/event\/[^/]+\/ticket-types$/, '/event/:eventId/ticket-types')
		.replace(/\/event\/[^/]+\/banner$/, '/event/:eventId/banner')
		.replace(/\/event\/[^/]+$/, '/event/:eventId')
		.replace(/\/events\/[^/]+$/, '/events/:eventId')
		.replace(/\/organizerpage\/?$/, '/organizerpage')
		.replace(/\/support\/complaint$/, '/support/complaint')
		.replace(/\/place\/[^/]+$/, '/place/:placeId')
		.replace(/\/user\/[^/]+$/, '/user/:userId')
		.replace(/\/users\/[^/]+$/, '/users/:userId')
		.replace(/\/wallet\/[^/]+\/validate$/, '/wallet/:walletId/validate')
		.replace(/\/wallet\/[^/]+$/, '/wallet/:walletId')
		.replace(/\/otp\/(?!send$|validate$)[^/]+$/, '/otp/:otpId');
};

const publicEventContext = (
	step: SentryPublicEventApiContext['step'],
	action: string,
	errorDomain: SentryPublicEventErrorDomain
): SentryPublicEventApiContext => ({
	area: 'event_page',
	flow: 'public_event',
	step,
	action,
	errorDomain,
});

export const getSentryPublicEventApiContext = ({
	routeTemplate,
	method,
	routeContext,
}: PublicEventApiContextInput): SentryPublicEventApiContext | null => {
	if (routeContext?.area !== 'event_page') return null;

	const route = routeTemplate.startsWith('/')
		? routeTemplate
		: `/${routeTemplate}`;
	const normalizedMethod = method.toUpperCase();

	if (route === '/eventinfo/:eventId') {
		return publicEventContext('initial_load', 'load_event', 'event');
	}
	if (route === '/tickettype') {
		return publicEventContext('tickets', 'load_ticket_types', 'tickets');
	}
	if (route === '/organizerpage') {
		return publicEventContext('producer', 'load_producer', 'producer');
	}
	if (route === '/me/billingdata/check') {
		return publicEventContext('producer', 'load_owner_status', 'producer');
	}
	if (route === '/sell/open') {
		return publicEventContext('cart', 'open_checkout', 'inventory');
	}
	if (route === '/event/:eventId/coupons/:coupon') {
		return publicEventContext('tickets', 'validate_coupon', 'coupon');
	}
	if (route === '/event/:eventId' && normalizedMethod === 'POST') {
		return publicEventContext('contact', 'send_contact', 'contact');
	}
	if (route === '/support/complaint') {
		return publicEventContext('complaint', 'send_complaint', 'complaint');
	}

	return null;
};

export const getSentryPublicEventErrorDomain = (
	errorCode: string | undefined,
	fallback: SentryPublicEventErrorDomain
): SentryPublicEventErrorDomain => {
	if (!errorCode) return fallback;

	const normalizedCode = errorCode.toLowerCase();

	if (/ticket|batch|quantity|price|sell/.test(normalizedCode)) {
		return 'tickets';
	}
	if (/coupon|discount/.test(normalizedCode)) return 'coupon';
	if (/producer|organizer|billing|recipient|account/.test(normalizedCode)) {
		return 'producer';
	}
	if (/whatsapp|contact|message/.test(normalizedCode)) return 'contact';
	if (/complaint|support|reason/.test(normalizedCode)) return 'complaint';
	if (/stock|inventory|sold/.test(normalizedCode)) return 'inventory';

	return fallback;
};

const eventManagementContext = (
	step: SentryEventManagementStep,
	action: string,
	errorDomain: SentryEventManagementErrorDomain
): SentryEventManagementApiContext => ({
	area: 'event_management',
	flow: 'manage_event',
	step,
	action,
	errorDomain,
});

const getEventManagementErrorDomainFromStep = (
	step: SentryEventManagementStep
): SentryEventManagementErrorDomain => {
	if (step === 'overview') return 'dashboard';
	if (step === 'guest_lists') return 'guests';
	if (step === 'unknown') return 'unknown';

	return step;
};

export const getSentryEventManagementApiContext = ({
	routeTemplate,
	method,
	routeContext,
}: EventManagementApiContextInput): SentryEventManagementApiContext | null => {
	if (routeContext?.area !== 'event_management') return null;

	const route = routeTemplate.startsWith('/')
		? routeTemplate
		: `/${routeTemplate}`;
	const normalizedMethod = method.toUpperCase();
	const currentStep = routeContext.step as SentryEventManagementStep;

	if (route === '/eventinfo/:eventId' || route === '/event/:eventId') {
		return eventManagementContext(currentStep, 'load_event', 'event');
	}
	if (route === '/event/:eventId/reports') {
		return eventManagementContext('reports', 'load_reports', 'reports');
	}
	if (route === '/event/:eventId/sales-origins') {
		return eventManagementContext('sales', 'load_sales_origins', 'sales');
	}
	if (route === '/event/:eventId/coupons') {
		return eventManagementContext(
			'coupons',
			normalizedMethod === 'GET' ? 'load_coupons' : 'save_coupon',
			'coupons'
		);
	}
	if (route === '/event/:eventId/coupons/:coupon') {
		return eventManagementContext('coupons', 'manage_coupon', 'coupons');
	}
	if (route === '/event/:eventId/restricted-link') {
		return eventManagementContext(
			'private_tickets',
			normalizedMethod === 'GET' ? 'load_private_links' : 'save_private_link',
			'private_tickets'
		);
	}
	if (route === '/event/:eventId/restricted-link/:linkName') {
		return eventManagementContext(
			'private_tickets',
			'manage_private_link',
			'private_tickets'
		);
	}
	if (route === '/event/:eventId/promoters') {
		return eventManagementContext('promoters', 'manage_promoters', 'promoters');
	}
	if (route === '/event/:eventId/collaborators') {
		return eventManagementContext(
			'collaborators',
			'manage_collaborators',
			'collaborators'
		);
	}
	if (route === '/event/:eventId/guests') {
		return eventManagementContext('guests', 'manage_guests', 'guests');
	}
	if (route === '/checkin/:eventId') {
		return eventManagementContext('checkin', 'manage_checkin', 'checkin');
	}
	if (route === '/cart-recovery/metrics/:eventId') {
		return eventManagementContext(
			'cart_recovery',
			'load_cart_recovery_metrics',
			'cart_recovery'
		);
	}
	if (route.startsWith('/cart-recovery')) {
		return eventManagementContext(
			'cart_recovery',
			'manage_cart_recovery',
			'cart_recovery'
		);
	}
	if (route === '/tickettype') {
		return eventManagementContext(currentStep, 'load_ticket_types', 'event');
	}

	return eventManagementContext(
		currentStep,
		`api_${normalizedMethod.toLowerCase()}`,
		getEventManagementErrorDomainFromStep(currentStep)
	);
};

export const getSentryEventManagementErrorDomain = (
	errorCode: string | undefined,
	fallback: SentryEventManagementErrorDomain
): SentryEventManagementErrorDomain => {
	if (!errorCode) return fallback;

	const normalizedCode = errorCode.toLowerCase();

	if (/coupon|discount/.test(normalizedCode)) return 'coupons';
	if (/checkin|check_in|ticket_validation/.test(normalizedCode)) return 'checkin';
	if (/guest|invite|participant/.test(normalizedCode)) return 'guests';
	if (/promoter/.test(normalizedCode)) return 'promoters';
	if (/collaborator|permission|role/.test(normalizedCode)) {
		return 'collaborators';
	}
	if (/restricted|private/.test(normalizedCode)) return 'private_tickets';
	if (/cart.?recovery|cadence|abandoned/.test(normalizedCode)) {
		return 'cart_recovery';
	}
	if (/report|export/.test(normalizedCode)) return 'reports';
	if (/sale|order|sold|payment|refund|cancel/.test(normalizedCode)) {
		return 'orders';
	}
	if (/integration|pixel|analytics|ads/.test(normalizedCode)) {
		return 'integrations';
	}
	if (/event|tickettype|ticket_type/.test(normalizedCode)) return 'event';

	return fallback;
};

const eventCreationContext = (
	flow: SentryEventCreationApiContext['flow'],
	step: SentryEventCreationApiContext['step'],
	action: string,
	errorDomain: SentryEventCreationErrorDomain,
	authAction?: SentryEventCreationApiContext['authAction']
): SentryEventCreationApiContext => ({
	area: 'event_creation',
	flow,
	step,
	action,
	errorDomain,
	...(authAction ? { authAction } : {}),
});

export const getSentryEventCreationApiContext = ({
	routeTemplate,
	method,
	routeContext,
}: EventCreationApiContextInput): SentryEventCreationApiContext | null => {
	if (routeContext?.area !== 'event_creation') return null;

	const route = routeTemplate.startsWith('/')
		? routeTemplate
		: `/${routeTemplate}`;
	const normalizedMethod = method.toUpperCase();
	const flow =
		routeContext.flow === 'edit_event' ? 'edit_event' : 'create_event';
	const currentStep = (
		[
			'event_start',
			'event_bootstrap',
			'event_what',
			'event_when',
			'event_where',
			'event_how_much',
			'event_publish',
		].includes(routeContext.step)
			? routeContext.step
			: 'event_what'
	) as SentryEventCreationApiContext['step'];

	if (route === '/signin') {
		return eventCreationContext(flow, currentStep, 'login', 'auth', 'login');
	}
	if (route === '/oauth-signin') {
		return eventCreationContext(
			flow,
			currentStep,
			'oauth_login',
			'auth',
			'oauth'
		);
	}
	if (route === '/user' && normalizedMethod === 'POST') {
		return eventCreationContext(flow, currentStep, 'signup', 'auth', 'signup');
	}
	if (route.startsWith('/user/otp/')) {
		const action = route.endsWith('/send') ? 'send_otp' : 'validate_otp';
		return eventCreationContext(flow, currentStep, action, 'auth', 'otp');
	}

	if (route === '/eventcategory') {
		return eventCreationContext(flow, 'event_what', 'load_categories', 'event');
	}
	if (route === '/event/drafts') {
		return eventCreationContext(
			'create_event',
			'event_start',
			'list_drafts',
			'draft'
		);
	}
	if (route === '/event/publish') {
		return eventCreationContext(
			flow,
			'event_publish',
			'publish_draft',
			'publication'
		);
	}
	if (route === '/' && normalizedMethod === 'POST') {
		return eventCreationContext(
			'create_event',
			'event_bootstrap',
			'create_draft',
			'draft'
		);
	}
	if (route === '/:draftId' && normalizedMethod === 'GET') {
		return eventCreationContext(flow, currentStep, 'load_draft', 'draft');
	}
	if (route === '/:draftId' && normalizedMethod === 'PATCH') {
		return eventCreationContext(flow, currentStep, 'update_draft', 'draft');
	}
	if (route === '/:eventId/draft') {
		return eventCreationContext(
			flow,
			currentStep,
			normalizedMethod === 'DELETE' ? 'delete_draft' : 'create_edit_draft',
			'draft'
		);
	}
	if (route === '/:draftId/sessions') {
		return eventCreationContext(
			flow,
			'event_when',
			'load_sessions',
			'schedule'
		);
	}
	if (route === '/:draftId/session') {
		return eventCreationContext(
			flow,
			'event_when',
			'create_session',
			'schedule'
		);
	}
	if (route === '/:draftId/session/:sessionId') {
		return eventCreationContext(
			flow,
			'event_when',
			normalizedMethod === 'DELETE' ? 'delete_session' : 'update_session',
			'schedule'
		);
	}
	if (route === '/defaults/ticket') {
		return eventCreationContext(
			flow,
			'event_how_much',
			'load_ticket_defaults',
			'tickets'
		);
	}
	if (route === '/:draftId/tickets') {
		return eventCreationContext(
			flow,
			'event_how_much',
			'load_tickets',
			'tickets'
		);
	}
	if (route === '/:draftId/tickets/sort') {
		return eventCreationContext(
			flow,
			'event_how_much',
			'sort_tickets',
			'tickets'
		);
	}
	if (route === '/:draftId/ticket') {
		return eventCreationContext(
			flow,
			'event_how_much',
			'create_ticket',
			'tickets'
		);
	}
	if (route === '/:draftId/ticket/:ticketId') {
		return eventCreationContext(
			flow,
			'event_how_much',
			normalizedMethod === 'DELETE' ? 'delete_ticket' : 'update_ticket',
			'tickets'
		);
	}
	if (route === '/:draftId/ticket/:ticketId/close') {
		return eventCreationContext(
			flow,
			'event_how_much',
			'close_ticket',
			'tickets'
		);
	}
	if (route === '/:draftId/ticket/:ticketId/resume') {
		return eventCreationContext(
			flow,
			'event_how_much',
			'resume_ticket',
			'tickets'
		);
	}
	if (route === '/:draftId/tax') {
		return eventCreationContext(
			flow,
			'event_how_much',
			'update_tax',
			'publication'
		);
	}
	if (route === '/:draftId/banner') {
		return eventCreationContext(
			flow,
			currentStep,
			normalizedMethod === 'DELETE' ? 'delete_banner' : 'upload_banner',
			'media'
		);
	}
	if (route === '/event/:eventId' && normalizedMethod === 'GET') {
		return eventCreationContext(flow, currentStep, 'load_event', 'event');
	}
	if (route === '/event' && normalizedMethod === 'POST') {
		return eventCreationContext(
			'create_event',
			'event_how_much',
			'create_event',
			'event'
		);
	}
	if (route === '/event/:eventId' && normalizedMethod === 'PUT') {
		return eventCreationContext(
			'edit_event',
			'event_how_much',
			'update_event',
			'event'
		);
	}
	if (route === '/event/:eventId/banner') {
		return eventCreationContext(flow, 'event_how_much', 'upload_banner', 'media');
	}
	if (route === '/place' && normalizedMethod === 'GET') {
		return eventCreationContext(flow, 'event_where', 'list_places', 'place');
	}
	if (route === '/place' && normalizedMethod === 'POST') {
		return eventCreationContext(flow, 'event_where', 'create_place', 'place');
	}
	if (route === '/place/:placeId') {
		const action =
			normalizedMethod === 'DELETE' ? 'remove_place' : 'update_place';
		return eventCreationContext(flow, 'event_where', action, 'place');
	}
	if (route === '/tickettype') {
		return eventCreationContext(
			flow,
			'event_how_much',
			'load_ticket_types',
			'tickets'
		);
	}

	return null;
};

export const getSentryEventCreationErrorDomain = (
	errorCode: string | undefined,
	fallback: SentryEventCreationErrorDomain
): SentryEventCreationErrorDomain => {
	if (!errorCode) return fallback;

	const normalizedCode = errorCode.toLowerCase();

	if (/otp|auth|token|user_status/.test(normalizedCode)) return 'auth';
	if (/publish|publication|tax/.test(normalizedCode)) return 'publication';
	if (/draft|stale/.test(normalizedCode)) return 'draft';
	if (/banner|image|file|media/.test(normalizedCode)) return 'media';
	if (/place|address|zipcode|zip_code|city|state/.test(normalizedCode)) {
		return 'place';
	}
	if (
		/(^|[_-])(schedule|session|date|time|start|end)([_-]|$)|startdate|enddate|datetime/.test(
			normalizedCode
		)
	) {
		return 'schedule';
	}
	if (/ticket|batch|quantity|price|sell/.test(normalizedCode)) {
		return 'tickets';
	}

	return fallback;
};

const payoutAccountContext = (
	step: SentryPayoutAccountStep,
	action: string,
	errorDomain: SentryPayoutAccountErrorDomain
): SentryPayoutAccountApiContext => ({
	area: 'payout_account',
	flow: 'recipient_registration',
	step,
	action,
	errorDomain,
});

export const getSentryPayoutAccountApiContext = ({
	routeTemplate,
	method,
	isPayoutAccountSurface,
	currentStep,
}: PayoutAccountApiContextInput): SentryPayoutAccountApiContext | null => {
	if (!isPayoutAccountSurface) return null;

	const route = routeTemplate.startsWith('/')
		? routeTemplate
		: `/${routeTemplate}`;
	const normalizedMethod = method.toUpperCase();

	if (route === '/whatsapp/validate') {
		return payoutAccountContext(
			'recipient_details',
			'validate_phone',
			'contact'
		);
	}
	if (route === '/banks') {
		return payoutAccountContext('bank_account', 'load_banks', 'integration');
	}
	if (route === '/me/billingdata/check') {
		return payoutAccountContext(
			'review_submit',
			'check_recipient_status',
			'status'
		);
	}
	if (route === '/me/billingdata/bank-account') {
		return payoutAccountContext(
			normalizedMethod === 'PUT' ? 'review_submit' : currentStep,
			normalizedMethod === 'PUT'
				? 'register_bank_account'
				: 'load_bank_account',
			'bank_account'
		);
	}
	if (route === '/me/billingdata') {
		return payoutAccountContext(
			normalizedMethod === 'PUT' ? 'review_submit' : currentStep,
			normalizedMethod === 'PUT'
				? 'save_recipient_details'
				: 'load_recipient_details',
			'identity'
		);
	}

	return null;
};

export const getSentryPayoutAccountErrorDomain = (
	errorCode: string | undefined,
	fallback: SentryPayoutAccountErrorDomain
): SentryPayoutAccountErrorDomain => {
	if (!errorCode) return fallback;

	const normalizedCode = errorCode.toLowerCase();
	if (/bank|account|branch|agency/.test(normalizedCode)) return 'bank_account';
	if (/address|zip|city|state|street/.test(normalizedCode)) return 'address';
	if (/phone|whatsapp|contact/.test(normalizedCode)) return 'contact';
	if (/status|pending|registration|refused/.test(normalizedCode)) return 'status';
	if (/document|cpf|cnpj|birth|name|recipient/.test(normalizedCode)) {
		return 'identity';
	}

	return fallback;
};

const checkoutContext = (
	step: SentryCheckoutApiContext['step'],
	action: string,
	errorDomain: SentryCheckoutErrorDomain,
	authAction?: SentryCheckoutApiContext['authAction']
): SentryCheckoutApiContext => ({
	area: 'checkout',
	flow: 'ticket_purchase',
	step,
	action,
	errorDomain,
	...(authAction ? { authAction } : {}),
});

export const getSentryCheckoutApiContext = ({
	routeTemplate,
	method,
	isCheckoutSurface,
}: CheckoutApiContextInput): SentryCheckoutApiContext | null => {
	const route = routeTemplate.startsWith('/')
		? routeTemplate
		: `/${routeTemplate}`;
	const normalizedMethod = method.toUpperCase();

	if (route === '/sell/open') {
		return checkoutContext('tickets', 'open_sell', 'inventory');
	}
	if (route === '/sell/:sellId/edit') {
		return checkoutContext('tickets', 'edit_sell', 'inventory');
	}
	if (route === '/sell/:sellId/close') {
		return checkoutContext('tickets', 'close_sell', 'state');
	}
	if (route === '/sell/:sellId/buy') {
		return checkoutContext('payment', 'buy', 'payment');
	}
	if (route === '/sell/:sellId/pay') {
		return checkoutContext('payment', 'pay', 'payment');
	}
	if (route === '/sell/:sellId/status') {
		return checkoutContext('pix', 'check_payment_status', 'payment');
	}

	if (!isCheckoutSurface) return null;

	if (route === '/signin') {
		return checkoutContext('login', 'login', 'auth', 'login');
	}
	if (route === '/oauth-signin') {
		return checkoutContext('login', 'oauth_login', 'auth', 'oauth');
	}
	if (route === '/user' && normalizedMethod === 'POST') {
		return checkoutContext('login', 'signup', 'auth', 'signup');
	}
	if (route.startsWith('/user/otp/')) {
		const action = route.endsWith('/send') ? 'send_otp' : 'validate_otp';
		return checkoutContext('otp', action, 'auth', 'otp');
	}
	if (route === '/sell/:sellId/discount/:coupon') {
		return checkoutContext('tickets', 'apply_coupon', 'coupon');
	}
	if (route === '/event/:eventId/coupons/:coupon') {
		return checkoutContext('tickets', 'validate_coupon', 'coupon');
	}
	if (route === '/wallet') {
		return checkoutContext(
			'payment',
			normalizedMethod === 'POST' ? 'add_card' : 'list_payment_methods',
			'payment'
		);
	}
	if (route === '/wallet/:walletId/validate') {
		return checkoutContext('payment', 'validate_card', 'payment');
	}
	if (route === '/wallet/:walletId') {
		const action =
			normalizedMethod === 'DELETE' ? 'remove_card' : 'get_payment_method';
		return checkoutContext('payment', action, 'payment');
	}

	return null;
};

export const getSentryAuthApiContext = ({
	routeTemplate,
	sanitizedUrl,
	method,
	isAuthApiTarget,
	isAuthSurface,
}: AuthApiContextInput): SentryAuthApiContext | null => {
	const normalizedMethod = method.toUpperCase();
	const route = routeTemplate.startsWith('/')
		? routeTemplate
		: `/${routeTemplate}`;

	if (route === '/signin') {
		return { area: 'auth', flow: 'login', step: 'signin' };
	}

	if (route === '/oauth-signin') {
		return { area: 'auth', flow: 'oauth', step: 'oauth_signin' };
	}

	if (route === '/signout') {
		return { area: 'auth', flow: 'logout', step: 'signout' };
	}

	if (route === '/passwordrecovery') {
		return { area: 'auth', flow: 'recovery', step: 'recover_password' };
	}

	if (route === '/user' && normalizedMethod === 'POST') {
		return { area: 'auth', flow: 'signup', step: 'signup' };
	}

	if (
		route === '/user' &&
		normalizedMethod === 'GET' &&
		/[?&]mailcheck(?:[=&]|$)/.test(sanitizedUrl)
	) {
		return { area: 'auth', flow: 'login', step: 'check_user' };
	}

	if (isAuthSurface && route.startsWith('/user/otp/')) {
		const routeSegments = route.split('/').filter(Boolean);
		const otpStep = routeSegments[routeSegments.length - 1] ?? 'unknown';
		return { area: 'auth', flow: 'otp', step: otpStep };
	}

	if (isAuthApiTarget) {
		return { area: 'auth', flow: 'auth', step: 'api' };
	}

	return null;
};
